import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUp } from "lucide-react";
import ArtLightbox from "../components/ArtLightbox";
import { VEHICLE_FILES } from "../data/vehicles";

const SECTION_WRAP = "max-w-[1180px] mx-auto px-6 md:px-8";
const EASE = [0.16, 1, 0.3, 1];

const FileBlock = ({ title, lines, testid }) => (
  <div data-testid={testid}>
    <div className="font-caps text-[10px] text-[var(--cb-gold)]">{title}</div>
    <div className="h-px w-8 bg-[var(--cb-gold)] opacity-60 mt-2.5 mb-4" />
    <div className="space-y-3">
      {lines.map((line, i) => (
        <p key={i} className="font-serif text-[14.5px] text-[var(--cb-text)] leading-[1.75]">{line}</p>
      ))}
    </div>
  </div>
);

const VehicleFile = ({ vehicle, index, onArt }) => (
  <motion.article
    id={`vehicle-${vehicle.slug}`}
    data-testid={`vehicle-card-${vehicle.slug}`}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: EASE }}
    style={{ scrollMarginTop: "110px" }}
    className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start pt-14 border-t border-[var(--cb-divider)] first:border-0 first:pt-0"
  >
    <div className="lg:col-span-5">
      <div className="space-y-4">
        {[vehicle.image, vehicle.image2].filter(Boolean).map((src, imageIndex) => (
          <button
            key={src}
            type="button"
            data-testid={imageIndex === 0 ? `vehicle-art-${vehicle.slug}` : `vehicle-art-${vehicle.slug}-${imageIndex + 1}`}
            onClick={() => onArt({ src, label: vehicle.name, sub: vehicle.tag })}
            aria-label={`Enlarge ${vehicle.name} artwork${imageIndex > 0 ? ` ${imageIndex + 1}` : ""}`}
            className="block w-full cursor-pointer group relative overflow-hidden cb-card"
          >
            <img
              src={src}
              alt={`${vehicle.name} — ${vehicle.tag}`}
              loading="lazy"
              className="block w-full h-auto transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
            />
            <div className="absolute inset-x-0 bottom-0 px-5 pb-4 pt-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                 style={{ background: "linear-gradient(0deg, rgba(5,5,8,0.85) 0%, transparent 100%)" }}>
              <span className="block font-caps text-[9px] text-[var(--cb-gold)]">CLICK TO ENLARGE</span>
            </div>
          </button>
        ))}
      </div>
    </div>

    <div className="lg:col-span-7">
      <div className="font-caps text-[10px] text-[var(--cb-text-muted)]">VEHICLE FILE {String(index + 1).padStart(2, "0")}</div>
      <h2 className="font-headline text-[var(--cb-text)] text-[26px] md:text-[34px] uppercase tracking-[0.04em] mt-3">{vehicle.name}</h2>
      <div className="font-caps text-[10px] text-[var(--cb-gold)] mt-2.5">{vehicle.tag}</div>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8 mt-9">
        <FileBlock title="THE MACHINE" lines={vehicle.machine} testid={`vehicle-machine-${vehicle.slug}`} />
        <FileBlock title="THE BUILD" lines={vehicle.build} testid={`vehicle-build-${vehicle.slug}`} />
        <FileBlock title="THE ABILITY" lines={vehicle.ability} testid={`vehicle-ability-${vehicle.slug}`} />
        <FileBlock title="THE STORY" lines={vehicle.story} testid={`vehicle-story-${vehicle.slug}`} />
      </div>
    </div>
  </motion.article>
);

const StickyVehicleIndex = ({ activeSlug }) => (
  <aside data-testid="vehicle-sticky-index" aria-label="Vehicle files index"
         className="hidden xl:block fixed left-4 top-1/2 -translate-y-1/2 z-30">
    <div className="border-l border-[var(--cb-divider)] pl-3 py-3 bg-black/25 backdrop-blur-sm">
      <div className="font-caps text-[8px] text-[var(--cb-gold)] mb-3">FILES</div>
      <nav className="space-y-1.5">
        {VEHICLE_FILES.map((vehicle, index) => {
          const active = activeSlug === vehicle.slug;
          return (
            <a
              key={vehicle.slug}
              href={`#vehicle-${vehicle.slug}`}
              data-testid={`vehicle-index-${vehicle.slug}`}
              aria-current={active ? "true" : undefined}
              className={`group flex items-center gap-2 font-caps text-[8px] tracking-[0.16em] transition-colors duration-300 ${active ? "text-[var(--cb-gold)]" : "text-[var(--cb-text-muted)] hover:text-[var(--cb-gold)]"}`}
            >
              <span className={`h-px transition-all duration-300 ${active ? "w-4 bg-[var(--cb-gold)]" : "w-2 bg-[var(--cb-divider)] group-hover:bg-[var(--cb-gold)]"}`} />
              <span className="max-w-[62px] truncate">{String(index + 1).padStart(2, "0")} · {vehicle.name}</span>
            </a>
          );
        })}
      </nav>
    </div>
  </aside>
);


const Vehicles = () => {
  const [activeArt, setActiveArt] = useState(null);
  const [activeVehicle, setActiveVehicle] = useState(VEHICLE_FILES[0].slug);

  useEffect(() => {
    const updateActive = () => {
      let current = VEHICLE_FILES[0].slug;
      VEHICLE_FILES.forEach((vehicle) => {
        const el = document.getElementById(`vehicle-${vehicle.slug}`);
        if (el && el.getBoundingClientRect().top <= 180) current = vehicle.slug;
      });
      setActiveVehicle(current);
    };
    updateActive();
    window.addEventListener("scroll", updateActive, { passive: true });
    return () => window.removeEventListener("scroll", updateActive);
  }, []);

  return (
    <div data-testid="page-vehicles" className="bg-[var(--cb-bg)]">
      <StickyVehicleIndex activeSlug={activeVehicle} />

      <section data-testid="vehicles-hero" className="pt-16 pb-14 md:pt-20 md:pb-16 border-b border-[var(--cb-divider)]"
               style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(184,138,44,0.14) 0%, transparent 55%), linear-gradient(180deg, #0E0B14 0%, var(--cb-bg) 100%)" }}>
        <div className={SECTION_WRAP}>
          <Link to="/" data-testid="vehicles-back-home"
                className="inline-flex items-center gap-2 font-caps text-[10px] text-[var(--cb-text-dim)] hover:text-[var(--cb-gold)] transition-colors duration-300">
            <ArrowLeft size={14} /> BACK TO CHERUBIM
          </Link>
          <div className="text-center mt-12">
            <div className="font-caps-wide text-[11px] text-[var(--cb-gold)]">SEASON ONE · INHERITANCE & ARMOR ARCHIVE</div>
            <h1 className="font-headline text-[var(--cb-text)] text-[38px] md:text-[58px] uppercase tracking-[0.04em] mt-5 leading-tight">
              The Vehicle Files
            </h1>
            <p className="font-serif italic text-[var(--cb-text-dim)] text-[17px] md:text-[19px] mt-5 max-w-[760px] mx-auto">
              Cam's inherited fleet, tactical interceptors, and the machines shaping Memphis.
            </p>
            <div className="h-px w-16 bg-[var(--cb-gold)] mt-7 mx-auto" />
          </div>

          <div className="flex flex-wrap justify-center gap-2 mt-10" data-testid="vehicle-jump-index">
            {VEHICLE_FILES.map((vehicle) => (
              <a key={vehicle.slug} href={`#vehicle-${vehicle.slug}`} data-testid={`vehicle-jump-${vehicle.slug}`}
                 className="font-caps text-[9px] px-3.5 py-2 border border-[var(--cb-divider)] text-[var(--cb-text-dim)] hover:text-[var(--cb-gold)] hover:border-[var(--cb-gold)] transition-colors duration-300">
                {vehicle.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="vehicles-collection" className="py-16 md:py-20">
        <div className={`${SECTION_WRAP} space-y-20`}>
          {VEHICLE_FILES.map((vehicle, index) => (
            <VehicleFile key={vehicle.slug} vehicle={vehicle} index={index} onArt={setActiveArt} />
          ))}
        </div>
      </section>

      <div className="pb-16 text-center">
        <button type="button" data-testid="vehicles-back-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="btn-outline-cb inline-flex items-center gap-2">
          BACK TO TOP <ArrowUp size={14} />
        </button>
      </div>

      <ArtLightbox active={activeArt} onClose={() => setActiveArt(null)} testid="vehicle-lightbox" />
    </div>
  );
};

export default Vehicles;
